import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormGroup, Input, Row, Col, Spinner } from "reactstrap";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  getRangeList,
  addFund,
  leoPayIn,
  showMessage,
  clearAddFundSuccess,
} from "Store/Reducer/common.slice";
import { api } from "../../../utils/api";
import useAppLanguage from "hooks/useAppLanguage";

const UPLOAD_API_ORIGIN = "https://fxbazzar.com:2025";

function normalizeUploadApiOrigin(origin) {
  const o = String(origin || "").trim().replace(/\/+$/, "");
  if (!o) return UPLOAD_API_ORIGIN;
  try {
    const u = new URL(o.includes("://") ? o : `https://${o}`);
    if (/^(?:www\.)?fxbazzar\.com$/i.test(u.hostname)) {
      if (!u.port || u.port === "80" || u.port === "443") return UPLOAD_API_ORIGIN;
    }
    return `${u.protocol}//${u.host}`.replace(/\/+$/, "");
  } catch {
    return o;
  }
}

/** Prefer relative DB paths; resolve against API that serves /Images (not website :443). */
function resolveUploadApiOrigin() {
  const fromEnv = String(process.env.REACT_APP_UPLOAD_ORIGIN || "").trim().replace(/\/+$/, "");
  if (fromEnv) return normalizeUploadApiOrigin(fromEnv);

  // Local npm start: QR files are on this PC; setupProxy forwards /Images to local API.
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    return window.location.origin;
  }

  const base = String(api.defaults.baseURL || "").trim();
  if (/^https?:\/\//i.test(base)) {
    return normalizeUploadApiOrigin(base.replace(/\/api\/v1\/?$/i, ""));
  }

  if (typeof window !== "undefined") {
    return normalizeUploadApiOrigin(window.location.origin);
  }

  return UPLOAD_API_ORIGIN;
}

function resolveBankUpiImageUrl(range) {
  const raw = String(range?.bankUPIImage || range?.BankUPIImage || "").trim();
  if (!raw) return "";
  if (/^data:/i.test(raw)) return raw;

  const apiOrigin = resolveUploadApiOrigin();
  const pathMatch = raw.match(/(\/Images\/UploadBankImages\/[^?#]+)/i);
  if (pathMatch) return `${apiOrigin}${pathMatch[1]}`;

  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      if (/UploadBankImages/i.test(u.pathname)) {
        return `${normalizeUploadApiOrigin(u.origin)}${u.pathname}`;
      }
    } catch {
      /* keep */
    }
    return raw;
  }

  if (raw.startsWith("/Images/")) return `${apiOrigin}${raw}`;
  if (raw.includes("UploadBankImages/")) {
    return `${apiOrigin}/${raw.replace(/^\/+/, "")}`;
  }
  return `${apiOrigin}/Images/UploadBankImages/${raw.replace(/^\/+/, "")}`;
}

const BANK_ICON =
  "https://cdn-icons-png.flaticon.com/512/2830/2830284.png";
const PAYMENT_ICON =
  "https://cdn-icons-png.flaticon.com/512/4021/4021708.png";

function OptionRow({ title, subtitle, selected, onClick, iconImg }) {
  return (
    <button
      type="button"
      className={`add-point-option${selected ? " add-point-option--selected" : ""}`}
      onClick={onClick}
    >
      <span className="add-point-option__icon">
        {iconImg ? <img src={iconImg} alt="" /> : null}
      </span>
      <span className="add-point-option__text">
        <span className="add-point-option__title">{title}</span>
        {subtitle ? <span className="add-point-option__sub">{subtitle}</span> : null}
      </span>
      {selected ? <span className="add-point-option__check">✓</span> : null}
    </button>
  );
}

function RangeSkeleton() {
  return (
    <div className="add-point-skeleton" aria-hidden>
      {[1, 2, 3].map((k) => (
        <div key={k} className="add-point-skeleton__row" />
      ))}
    </div>
  );
}

function AddPointShell({ embedded, onClose, sheetClass, children }) {
  const sheet = `modal-box add-point-sheet${sheetClass ? ` ${sheetClass}` : ""}`.trim();
  if (embedded) {
    return (
      <div
        className={`wallet-add-embed wallet-mobile-flow ${sheet}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    );
  }
  return (
    <div className="modal-overlay add-point-overlay" onClick={onClose}>
      <div className={sheet} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export default function AddPointFlow({ onClose, user, onSuccess, embedded = false }) {
  const dispatch = useDispatch();
  const { t } = useAppLanguage();
  const formRef = useRef(null);
  const {
    amountRangeList,
    whatappRecords,
    paymentGetway,
    addFundLoading,
    isAddFund,
    rangeListLoading,
  } = useSelector((s) => s.common);

  const [step, setStep] = useState("pick");
  const [selectedRange, setSelectedRange] = useState(null);
  const [activePayTab, setActivePayTab] = useState("upi");
  const [copySuccess, setCopySuccess] = useState("");
  const [leoamount, setleoAmount] = useState("");
  const [qrPreviewOpen, setQrPreviewOpen] = useState(false);
  const upiQrSrc = resolveBankUpiImageUrl(selectedRange);

  const isMasterRole = user?.isMasterRole !== false;
  const hasRangeData =
    (amountRangeList?.length || 0) +
      (paymentGetway?.length || 0) +
      (whatappRecords?.length || 0) >
    0;
  const showPickLoading = rangeListLoading && !hasRangeData;

  useEffect(() => {
    dispatch(clearAddFundSuccess());
  }, [dispatch]);

  useEffect(() => {
    if (user?.userId) dispatch(getRangeList());
  }, [dispatch, user?.userId]);

  useEffect(() => {
    if (!isAddFund) return;
    dispatch(clearAddFundSuccess());
    onSuccess?.();
    onClose();
  }, [isAddFund, dispatch, onClose, onSuccess]);

  const handleRangeSelect = (item) => setSelectedRange(item);

  const handlePickSubmit = () => {
    if (!selectedRange) return;
    if (selectedRange?.paymentGetwayName === "LeoPay") {
      setStep("leo");
      setleoAmount("");
      return;
    }
    if (selectedRange?.whatsAppNumber) {
      const url = `https://wa.me/${selectedRange.whatsAppNumber}?text=Hello,%20I%20want%20to%20add%20points.`;
      window.open(url, "_blank");
      return;
    }
    setStep("pay");
    setActivePayTab("upi");
    setCopySuccess("");
    setQrPreviewOpen(false);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    dispatch(showMessage({ message: t("copiedToClipboard"), varient: "success" }));
    setCopySuccess(text);
  };

  const initialValues = {
    transactionId: "",
    amount: "",
    remark: "",
    transactionFile: null,
  };

  const validationSchema =
    selectedRange &&
    Yup.object().shape({
      transactionId: Yup.string().required(t("txnIdRequired")),
      amount: Yup.number()
        .required(t("amountRequired"))
        .positive(t("amountPositive"))
        .test(
          "range",
          t("amountMustBeBetween")
            .replace("{min}", selectedRange.startAmountRange)
            .replace("{max}", selectedRange.endAmountRange),
          (v) =>
            v >= selectedRange.startAmountRange &&
            v <= selectedRange.endAmountRange
        ),
      remark: Yup.string().optional(),
      transactionFile: Yup.mixed().required(t("paymentReceiptRequired")),
    });

  const backToPick = () => {
    setStep("pick");
    setSelectedRange(null);
    setQrPreviewOpen(false);
  };

  if (!isMasterRole) {
    return (
      <AddPointShell embedded={embedded} onClose={onClose} sheetClass="">
        <div className="modal-header">
          <h3>{t("addPoint")}</h3>
          {!embedded && (
            <button type="button" className="modal-close" onClick={onClose} aria-label={t("close")}>
              ✕
            </button>
          )}
        </div>
        <div className="modal-body">
          <p className="add-point-hint">{t("addPointUnavailable")}</p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-confirm" onClick={onClose}>
            {t("ok")}
          </button>
        </div>
      </AddPointShell>
    );
  }

  if (step === "leo") {
    return (
      <AddPointShell embedded={embedded} onClose={onClose} sheetClass="">
        <div className="modal-header">
          <h3>{t("payOnline")}</h3>
          {!embedded && (
            <button type="button" className="modal-close" onClick={onClose} aria-label={t("close")}>
              ✕
            </button>
          )}
        </div>
          <div className="modal-body">
            {addFundLoading && (
              <div className="add-point-inline-loading">
                <Spinner size="sm" color="secondary" />
              </div>
            )}
            <div className="modal-field">
              <label htmlFor="leoamount">{t("amountMin500")}</label>
              <input
                id="leoamount"
                type="number"
                className="form-control add-point-leo-field"
                min={500}
                value={leoamount}
                onChange={(e) => setleoAmount(e.target.value)}
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
                }}
                placeholder={t("enterAmount")}
              />
            </div>
            {leoamount && Number(leoamount) < 500 && (
              <p className="add-point-error">{t("minAmount500")}</p>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={backToPick}>
              {t("back")}
            </button>
            <button
              type="button"
              className="btn-confirm"
              disabled={!leoamount || Number(leoamount) < 500}
              onClick={() => dispatch(leoPayIn({ amount: leoamount }))}
            >
              {t("submitAndPay")}
            </button>
          </div>
      </AddPointShell>
    );
  }

  if (step === "pay" && selectedRange) {
    return (
      <AddPointShell
        embedded={embedded}
        onClose={onClose}
        sheetClass="add-point-sheet--wide add-point-sheet--payment"
      >
          {!embedded && (
            <div className="modal-header add-point-pay-header">
              <h3>{t("makePayment")}</h3>
              <button type="button" className="modal-close" onClick={onClose} aria-label={t("close")}>
                ✕
              </button>
            </div>
          )}
          <div className="modal-body add-point-pay-body wallet-mobile-flow__scroll">
            {addFundLoading && (
              <div className="add-point-pay-loading">
                <Spinner color="secondary" />
                <span>{t("submitting")}</span>
              </div>
            )}
            <div className="add-point-tabs wallet-mobile-segment">
              <button
                type="button"
                className={activePayTab === "upi" ? "active" : ""}
                onClick={() => {
                  setActivePayTab("upi");
                  formRef.current?.resetForm();
                }}
              >
                UPI
              </button>
              <button
                type="button"
                className={activePayTab === "bank" ? "active" : ""}
                onClick={() => {
                  setActivePayTab("bank");
                  formRef.current?.resetForm();
                }}
              >
                {t("bankTransfer")}
              </button>
            </div>

            {activePayTab === "upi" && (
              <div className="add-point-detail-card add-point-detail-card--pay wallet-mobile-card wallet-mobile-pay-card">
                <div className="add-point-upi-row">
                  <div>
                    <span className="add-point-kv-label">UPI ID</span>
                    <div className="add-point-value-row">
                      <code className="add-point-highlight">{selectedRange.bankUPI || "—"}</code>
                      <button
                        type="button"
                        className="add-point-copy"
                        onClick={() => selectedRange.bankUPI && handleCopy(selectedRange.bankUPI)}
                      >
                        {t("copy")}
                      </button>
                    </div>
                    {copySuccess === selectedRange.bankUPI && (
                      <span className="add-point-copied">{t("copied")}</span>
                    )}
                  </div>
                  {upiQrSrc ? (
                    <button
                      type="button"
                      className="add-point-qr"
                      onClick={() => setQrPreviewOpen(true)}
                      title={t("openUpiQr")}
                      aria-label={t("openUpiQr")}
                    >
                      <img src={upiQrSrc} alt="UPI QR" />
                      <span className="add-point-qr__hint">{t("tapToEnlarge")}</span>
                    </button>
                  ) : null}
                </div>
                <p className="add-point-range-pill">
                  {t("allowed")} ₹{selectedRange.startAmountRange} – ₹{selectedRange.endAmountRange}
                </p>
              </div>
            )}

            {qrPreviewOpen && upiQrSrc ? (
              <div
                className="add-point-qr-lightbox"
                role="dialog"
                aria-modal="true"
                aria-label={t("upiQrCode")}
                onClick={() => setQrPreviewOpen(false)}
              >
                <div
                  className="add-point-qr-lightbox__panel"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="add-point-qr-lightbox__close"
                    onClick={() => setQrPreviewOpen(false)}
                    aria-label={t("closeQr")}
                  >
                    ✕
                  </button>
                  <img
                    src={upiQrSrc}
                    alt={t("upiQrCode")}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      const note = e.currentTarget.nextElementSibling;
                      if (note && note.dataset?.qrErr) note.style.display = "block";
                    }}
                  />
                  <p
                    data-qr-err="1"
                    className="add-point-qr-lightbox__missing"
                    style={{ display: "none" }}
                  >
                    {t("qrImageMissing")}
                  </p>
                  {selectedRange.bankUPI ? (
                    <p className="add-point-qr-lightbox__upi">{selectedRange.bankUPI}</p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {activePayTab === "bank" && (
              <div className="add-point-detail-card add-point-detail-card--pay wallet-mobile-card wallet-mobile-pay-card">
                <div className="add-point-bank-grid">
                  <div>
                    <span className="add-point-kv-label">{t("bank")}</span>
                    <p className="add-point-kv-value">{selectedRange.bankName}</p>
                  </div>
                  <div>
                    <span className="add-point-kv-label">{t("account")}</span>
                    <p className="add-point-value-row">
                      <code className="add-point-highlight">{selectedRange.bankAccountNumber}</code>
                      <button
                        type="button"
                        className="add-point-copy"
                        onClick={() =>
                          selectedRange.bankAccountNumber &&
                          handleCopy(selectedRange.bankAccountNumber)
                        }
                      >
                        {t("copy")}
                      </button>
                    </p>
                  </div>
                  <div>
                    <span className="add-point-kv-label">{t("beneficiary")}</span>
                    <p className="add-point-kv-value">{selectedRange.bankHolderName}</p>
                  </div>
                  <div>
                    <span className="add-point-kv-label">IFSC</span>
                    <p className="add-point-value-row">
                      <code className="add-point-highlight">{selectedRange.bankIFSC}</code>
                      <button
                        type="button"
                        className="add-point-copy"
                        onClick={() =>
                          selectedRange.bankIFSC && handleCopy(selectedRange.bankIFSC)
                        }
                      >
                        {t("copy")}
                      </button>
                    </p>
                  </div>
                </div>
                <p className="add-point-range-pill">
                  {t("allowed")} ₹{selectedRange.startAmountRange} – ₹{selectedRange.endAmountRange}
                </p>
              </div>
            )}

            <Formik
              innerRef={formRef}
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values) => dispatch(addFund(values))}
            >
              {({ setFieldValue, errors, touched }) => (
                <Form className="add-point-form wallet-mobile-form">
                  <section className="wallet-mobile-section">
                    <h4 className="wallet-mobile-section__title">{t("paymentProof")}</h4>
                    <div className="wallet-mobile-card">
                  <Row className="g-3">
                    <Col xs="12">
                      <FormGroup className="wallet-mobile-field">
                        <label className="add-point-field-label" htmlFor="add-point-txn-id">
                          {t("transactionId")}
                        </label>
                        <Field
                          as={Input}
                          id="add-point-txn-id"
                          name="transactionId"
                          placeholder={t("enterTransactionId")}
                          className="add-point-input"
                          invalid={touched.transactionId && !!errors.transactionId}
                        />
                        <span className="text-danger small">
                          <ErrorMessage name="transactionId" />
                        </span>
                      </FormGroup>
                    </Col>
                    <Col xs="12">
                      <FormGroup className="wallet-mobile-field">
                        <label className="add-point-field-label" htmlFor="add-point-amt">
                          {t("amountInr")}
                        </label>
                        <Field
                          as={Input}
                          id="add-point-amt"
                          type="number"
                          name="amount"
                          placeholder={t("amount")}
                          className="add-point-input"
                          invalid={touched.amount && !!errors.amount}
                          onWheel={(e) => e.target.blur()}
                          onKeyDown={(e) => {
                            if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
                          }}
                        />
                        <span className="text-danger small">
                          <ErrorMessage name="amount" />
                        </span>
                      </FormGroup>
                    </Col>
                    <Col xs="12">
                      <FormGroup className="wallet-mobile-field">
                        <label className="add-point-field-label" htmlFor="add-point-remark">
                          {t("remarkOptional")}
                        </label>
                        <Field
                          as={Input}
                          id="add-point-remark"
                          name="remark"
                          placeholder={t("remark")}
                          className="add-point-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col xs="12">
                      <FormGroup className="wallet-mobile-field wallet-mobile-field--last">
                        <label className="add-point-file-label" htmlFor="add-point-file">
                          {t("paymentScreenshot")}
                        </label>
                        <div className="wallet-mobile-file">
                        <Input
                          id="add-point-file"
                          type="file"
                          accept="image/*"
                          className="add-point-file"
                          onChange={(e) => {
                            const file = e.currentTarget.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () =>
                                setFieldValue(
                                  "transactionFile",
                                  reader.result.split(",")[1]
                                );
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                        </div>
                        <span className="text-danger small wallet-mobile-error">
                          <ErrorMessage name="transactionFile" />
                        </span>
                      </FormGroup>
                    </Col>
                  </Row>
                    </div>
                  </section>
                  <div className="wallet-mobile-form__footer add-point-form-footer">
                    {!embedded && (
                      <button type="button" className="btn-cancel" onClick={backToPick}>
                        {t("back")}
                      </button>
                    )}
                    <button type="submit" className="btn-confirm add-point-submit wallet-mobile-submit">
                      {t("submitRequest")}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
      </AddPointShell>
    );
  }

  return (
    <AddPointShell embedded={embedded} onClose={onClose} sheetClass="add-point-sheet--pick">
        {!embedded && (
          <div className="modal-header add-point-header">
            <div>
              <h3>{t("addPoint")}</h3>
              <p className="add-point-sub">{t("chooseHowAddFunds")}</p>
            </div>
            <button type="button" className="modal-close" onClick={onClose} aria-label={t("close")}>
              ✕
            </button>
          </div>
        )}
        <div className="modal-body add-point-pick-body wallet-mobile-flow__scroll">
          {rangeListLoading && hasRangeData && (
            <div className="add-point-refresh-bar">
              <Spinner size="sm" /> {t("updatingOptions")}
            </div>
          )}
          {showPickLoading ? (
            <RangeSkeleton />
          ) : !hasRangeData && !rangeListLoading ? (
            <p className="add-point-hint">{t("noPaymentOptions")}</p>
          ) : (
            <div className="add-point-option-list">
              {amountRangeList.map((item) => (
                <OptionRow
                  key={item.id}
                  iconImg={BANK_ICON}
                  title={item.bankName}
                  subtitle={`₹${item.startAmountRange} – ₹${item.endAmountRange}`}
                  selected={selectedRange?.id === item.id}
                  onClick={() => handleRangeSelect(item)}
                />
              ))}
              {paymentGetway?.map((gateway, idx) => (
                <OptionRow
                  key={`g-${idx}`}
                  iconImg={PAYMENT_ICON}
                  title={`${gateway.paymentGetwayName} — ${t("payOnline")}`}
                  selected={
                    selectedRange?.paymentGetwayName === gateway.paymentGetwayName
                  }
                  onClick={() => handleRangeSelect(gateway)}
                />
              ))}
              {whatappRecords?.map((w) => (
                <OptionRow
                  key={w.id}
                  iconImg="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                  title="WhatsApp"
                  subtitle={t("requestAddPointChat")}
                  selected={selectedRange?.id === w.id}
                  onClick={() => handleRangeSelect(w)}
                />
              ))}
            </div>
          )}
        </div>
        <div className={`modal-footer add-point-footer${embedded ? " wallet-mobile-form__footer" : ""}`}>
          <button
            type="button"
            className="btn-confirm add-point-continue wallet-mobile-submit"
            disabled={!selectedRange || showPickLoading}
            onClick={handlePickSubmit}
          >
            {t("continue")}
          </button>
        </div>
    </AddPointShell>
  );
}
